import React from 'react';

const ColorThemeDemo: React.FC = () => {
  const colorPalettes = [
    {
      name: 'Brasil',
      colors: [
        { name: 'Verde', class: 'bg-brazil-500', hex: '#22c55e' },
        { name: 'Verde Claro', class: 'bg-brazil-300', hex: '#86efac' },
        { name: 'Verde Escuro', class: 'bg-brazil-700', hex: '#15803d' },
      ]
    },
    {
      name: 'Portugal',
      colors: [
        { name: 'Vermelho', class: 'bg-portugal-500', hex: '#ef4444' },
        { name: 'Vermelho Claro', class: 'bg-portugal-300', hex: '#fca5a5' },
        { name: 'Vermelho Escuro', class: 'bg-portugal-700', hex: '#b91c1c' },
      ]
    },
    {
      name: 'Dourado',
      colors: [
        { name: 'Dourado', class: 'bg-gold-500', hex: '#f59e0b' },
        { name: 'Dourado Claro', class: 'bg-gold-300', hex: '#fcd34d' },
        { name: 'Dourado Escuro', class: 'bg-gold-700', hex: '#b45309' },
      ]
    }
  ];

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8 text-center">
          Tema de Cores: Brasil 🇧🇷 + Portugal 🇵🇹
        </h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {colorPalettes.map((palette, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
                {palette.name}
              </h2>
              
              <div className="space-y-4">
                {palette.colors.map((color, colorIndex) => (
                  <div key={colorIndex} className="flex items-center space-x-4">
                    <div className={`w-16 h-16 rounded-lg ${color.class} shadow-md`}></div>
                    <div>
                      <div className="font-medium text-gray-900">{color.name}</div>
                      <div className="text-sm text-gray-600 font-mono">{color.hex}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            Combinações Temáticas
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Combinação Brasil */}
            <div className="bg-gradient-to-br from-brazil-500 to-brazil-700 text-white p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Verde Brasil</h3>
              <p className="text-brazil-100">Representa a natureza e o crescimento</p>
            </div>
            
            {/* Combinação Portugal */}
            <div className="bg-gradient-to-br from-portugal-500 to-portugal-700 text-white p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Vermelho Portugal</h3>
              <p className="text-portugal-100">Representa a paixão e a tradição</p>
            </div>
            
            {/* Combinação Dourada */}
            <div className="bg-gradient-to-br from-gold-500 to-gold-700 text-white p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Dourado</h3>
              <p className="text-gold-100">Representa a prosperidade e o sucesso</p>
            </div>
            
            {/* Combinação Mista */}
            <div className="bg-gradient-to-br from-brazil-500 to-portugal-500 text-white p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Brasil + Portugal</h3>
              <p className="text-white/90">União das duas culturas</p>
            </div>
            
            {/* Combinação com Dourado */}
            <div className="bg-gradient-to-br from-brazil-500 to-gold-500 text-white p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Brasil + Dourado</h3>
              <p className="text-white/90">Natureza e prosperidade</p>
            </div>
            
            {/* Combinação Portugal + Dourado */}
            <div className="bg-gradient-to-br from-portugal-500 to-gold-500 text-white p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Portugal + Dourado</h3>
              <p className="text-white/90">Tradição e sucesso</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            Aplicação no Sistema
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cores Primárias</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-brazil-500 rounded"></div>
                  <span className="text-gray-700">Verde Brasil - Cor principal</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-portugal-500 rounded"></div>
                  <span className="text-gray-700">Vermelho Portugal - Cor secundária</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gold-500 rounded"></div>
                  <span className="text-gray-700">Dourado - Cor de destaque</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Uso Recomendado</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Botões principais: Verde Brasil</li>
                <li>• Botões secundários: Vermelho Portugal</li>
                <li>• Destaques e badges: Dourado</li>
                <li>• Gradientes: Combinações harmoniosas</li>
                <li>• Acessibilidade: Contraste adequado</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorThemeDemo;
